import { Banner, Toast } from "@shopify/polaris"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CSVDownloadButton } from "../components/CSVDownloadButton.jsx"
import { CSVExportSuggestion } from "../components/CSVExportSuggestion.jsx"
import Logo from "../components/Logo"
import { TopMenu } from "../components/TopMenu.jsx"
import { ImportModeSelector } from "../components/multiple/ImportModeSelector.jsx"
import { ImportStoreForm } from "../components/multiple/ImportStoreForm.jsx"
import { Multilink } from "../components/multiple/Multilink.jsx"
import { MultipleProductsErrorBanner } from "../components/multiple/MultipleProductsErrorBanner.jsx"
import { MultipleProductsImportModal } from "../components/multiple/MultipleProductsImportModal"
import UploadMultipleProducts from "../components/multiple/UploadMultipleProducts"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider"
import { pushCrispEvent } from "../utils/crisp.js"
import { validateUrl } from "../utils/validateUrl"
import { KopyPlusCTA } from "../components/ai/KopyPlusCTA.jsx"
import { usePostHog } from "posthog-js/react"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"

const Multiple = (props) => {
	const posthog = usePostHog()
	const { state, dispatch } = useContext(GlobalStateContext)
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const authenticatedFetch = useAuthenticatedFetch()

	const [loading, setLoading] = useState(false)
	const [fetchErrorCode, setFetchErrorCode] = useState("")
	const [fetchedProducts, setFetchedProducts] = useState([])
	// cleanupHTML runs a full DOMParser pass per product, so without memoizing
	// this every unrelated re-render of the page re-parsed the entire import.
	const cleanedProducts = useMemo(
		() =>
			fetchedProducts.map((p) => ({
				...p,
				body_html: cleanupHTML(p.body_html),
			})),
		[fetchedProducts],
	)
	const [modalIsOpen, setModalIsOpen] = useState(false)
	const [productsToUpload, setProductsToUpload] = useState({})
	const [readyToUpload, setReadyToUpload] = useState(false)

	const [csvDownloadUrl, setCsvDownloadUrl] = useState("")
	const [csvFilename, setCsvFilename] = useState("")

	const [failedImporting, setFailedImporting] = useState(false)
	const [readyToShowAdBanner, setReadyToShowAdBanner] = useState(false)

	const [importMode, setImportMode] = useState("storeUrl")
	const [productsUrl, setProductsUrl] = useState("")
	const [productUrls, setProductUrls] = useState([])
	const [showMultilinkToast, setShowMultilinkToast] = useState(true)
	const [multilinkProgress, setMultilinkProgress] = useState(null)

	const [bulkAIOrders, setBulkAIOrders] = useState([])

	const pollProductsFetch = async (store, productsUrl) => {
		return fetch("/api/productFetcher/pollProductsFetch", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				store: store,
				fetchUrl: validateUrl(productsUrl),
			}),
		})
			.then((res) => res.json())
			.then((pollingRes) => {
				if (pollingRes.status === "COMPLETED") {
					if (pollingRes.products && pollingRes.products.length > 0) {
						setLoading(false)
						setFetchedProducts(pollingRes.products)
						setModalIsOpen(true)
					} else {
						setLoading(false)
						setFetchErrorCode("check_products_fetch_empty")
					}
				} else if (pollingRes.status === "ERROR") {
					setLoading(false)
					setFetchErrorCode("check_products_fetch_status_error")
				} else {
					setTimeout(() => {
						pollProductsFetch(store, productsUrl)
					}, 2000)
				}
			})
			.catch((err) => {
				console.error("pollProductsFetch catched error", err)
				setLoading(false)
				setFetchErrorCode("check_products_fetch_unexpected_error")
			})
	}

	const getProductsFromStoreUrl = ({
		productsUrl,
		maximumChecked,
		paginateChecked,
		paginateFrom,
		paginateTo,
	}) => {
		setProductsUrl(productsUrl)
		setProductUrls([])

		setLoading(true)
		setFetchErrorCode("")
		setFetchedProducts([])
		setCsvDownloadUrl("")
		setCsvFilename("")
		setFailedImporting(false)

		pushCrispEvent("multiple_products_fetch_submit", { productsUrl })
		posthog?.capture(
			"multiple_products_from_store_or_collection_import_clicked",
		)

		authenticatedFetch("/api/productFetcher/getMultipleProducts", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				productsUrl: validateUrl(productsUrl),
				maximum: maximumChecked,
				...(paginateChecked ? { paginateFrom: paginateFrom } : {}),
				...(paginateChecked ? { paginateTo: paginateTo } : {}),
				store: state.shop,
			}),
		})
			.then((res) => res.json())
			.then((res) => {
				if (res.error) {
					setLoading(false)
					setFetchErrorCode("store_fetch_error")
				} else if (res.polling === true) {
					setTimeout(() => {
						pollProductsFetch(state.shop, productsUrl)
					}, 2000)
				} else if (res.products && res.products.length > 0) {
					setLoading(false)
					setFetchedProducts(res.products)
					setModalIsOpen(true)
				} else if (
					res.products &&
					"title" in res.products &&
					res.products.title !== ""
				) {
					setLoading(false)
					setFetchedProducts([res.products])
					setModalIsOpen(true)
				} else {
					setLoading(false)
					setFetchErrorCode("store_fetch_empty")
				}
			})
			.catch((err) => {
				console.error("getProductsFromStoreUrl catched error", err)
				setLoading(false)
				setFetchErrorCode("store_fetch_unexpected_error")
			})
	}

	// Large multilink imports are fetched in the background, so the poll can run
	// for many minutes. Bound it so a job that disappears server-side (e.g. a
	// deploy) surfaces an error instead of polling forever.
	const MULTILINK_POLL_INTERVAL_MS = 2000
	const MULTILINK_POLL_TIMEOUT_MS = 30 * 60 * 1000

	const pollMultilinkProductsFetch = async (store, fetchUrl, startedAt) => {
		if (Date.now() - startedAt > MULTILINK_POLL_TIMEOUT_MS) {
			setLoading(false)
			setFetchErrorCode("multilink_fetch_timeout")
			return
		}

		const pollAgain = () =>
			setTimeout(() => {
				pollMultilinkProductsFetch(store, fetchUrl, startedAt)
			}, MULTILINK_POLL_INTERVAL_MS)

		try {
			const res = await authenticatedFetch(
				"/api/productFetcher/pollProductsFetch",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					// Sent verbatim: an opaque server-issued key, not a URL.
					body: JSON.stringify({ store, fetchUrl }),
				},
			)
			const pollingRes = await res.json()

			if (pollingRes.status === "COMPLETED") {
				setLoading(false)
				if (pollingRes.products?.length > 0) {
					setFetchedProducts(pollingRes.products)
					setModalIsOpen(true)
				} else {
					setFetchErrorCode("multilink_fetch_empty")
				}
				return
			}

			if (pollingRes.status === "ERROR") {
				setLoading(false)
				setFetchErrorCode("multilink_fetch_error")
				return
			}

			// Keep the last known counts if this response carries none, so the
			// progress banner doesn't flicker back to empty.
			if (pollingRes.total) {
				setMultilinkProgress({
					completed: pollingRes.completed || 0,
					total: pollingRes.total,
				})
			}
			pollAgain()
		} catch (err) {
			// The job itself is unaffected by a failed poll, so retry rather than
			// discarding a fetch that may already be minutes in. The elapsed-time
			// check above bounds how long this can go on.
			console.error("pollMultilinkProductsFetch catched error", err)
			pollAgain()
		}
	}

	const getMultilinkProducts = (productUrls) => {
		setProductUrls(productUrls)
		setProductsUrl("")
		setMultilinkProgress(null)

		setLoading(true)
		setFetchErrorCode("")
		setFetchedProducts([])
		setCsvDownloadUrl("")
		setCsvFilename("")
		setFailedImporting(false)
		setShowMultilinkToast(true)

		pushCrispEvent("multilink_fetch_submit", {
			productUrls: JSON.stringify(productUrls),
		})
		posthog?.capture("multiple_products_multilink_import_clicked")

		authenticatedFetch("/api/productFetcher/getMultilinkProducts", {
			method: "POST",
			body: JSON.stringify({
				productUrls: productUrls,
				store: state.shop,
			}),
		})
			.then((res) => res.json())
			.then((res) => {
				if (res.error) {
					setLoading(false)
					setFetchErrorCode("multilink_fetch_error")
				} else if (res.polling === true && res.fetchUrl) {
					setMultilinkProgress({
						completed: 0,
						total: productUrls.length,
					})
					const startedAt = Date.now()
					setTimeout(() => {
						pollMultilinkProductsFetch(
							state.shop,
							res.fetchUrl,
							startedAt,
						)
					}, MULTILINK_POLL_INTERVAL_MS)
				} else if (res.products && res.products.length > 0) {
					setLoading(false)
					setFetchedProducts(res.products)
					setModalIsOpen(true)
				} else {
					setLoading(false)
					setFetchErrorCode("multilink_fetch_empty")
				}
			})
			.catch((err) => {
				console.error("getMultilinkProducts catched error", err)
				setLoading(false)
				setFetchErrorCode("multilink_fetch_unexpected_error")
			})
	}

	const submitProductsToUpload = (submittedProducts, bulkAIOrders) => {
		pushCrispEvent("multiple_products_import_submit", {
			productsUrl: productsUrl,
		})
		setProductsToUpload(submittedProducts)
		setReadyToUpload(true)
		setModalIsOpen(false)
		setLoading(true)
		setReadyToShowAdBanner(true)
		setBulkAIOrders(bulkAIOrders)
	}

	const onFinishedUploading = () => {
		setLoading(false)
		setReadyToUpload(false)
	}

	const onCsvExport = (csvUrl, filename) => {
		setModalIsOpen(false)
		setCsvDownloadUrl(csvUrl)
		setCsvFilename(filename)
	}

	useEffect(() => {
		const importUrl = searchParams.get("importUrl")

		if (!importUrl) return

		setProductsUrl(importUrl)
		getProductsFromStoreUrl({
			productsUrl: importUrl,
			maximumChecked: true,
			paginateChecked: false,
		})

		setTimeout(() => {
			searchParams.delete("importUrl")
			setSearchParams(searchParams)
		}, 1000)
	}, [])

	useEffect(() => {
		if (!state.hasValidKopyBasicSubscription) {
			navigate("/upgradebasic")
		}
	}, [])

	return (
		<div>
			<TopMenu goBack="/" storeSelector storeSelectorDisabled={loading} />

			<div
				style={{
					paddingTop: "3.5rem",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Logo />

				<div style={{ maxWidth: "40rem", width: "100%" }}>
					<div style={{ marginTop: "3rem" }}>
						<ImportModeSelector
							importMode={importMode}
							setImportMode={setImportMode}
							loading={loading}
						/>

						{importMode === "storeUrl" ? (
							<ImportStoreForm
								loading={loading}
								onSubmit={getProductsFromStoreUrl}
								productsUrl={productsUrl}
								setProductsUrl={setProductsUrl}
							/>
						) : importMode === "multilink" ? (
							<Multilink
								loading={loading}
								onSubmit={getMultilinkProducts}
							/>
						) : (
							""
						)}
					</div>

					{loading && multilinkProgress?.total ? (
						<div style={{ marginTop: "1rem" }}>
							<Banner
								status="info"
								title={`${t("multiple.fetching_products_in_background")}`}
							>
								<p>
									{multilinkProgress.completed}/
									{multilinkProgress.total}
								</p>
							</Banner>
						</div>
					) : (
						""
					)}

					<MultipleProductsErrorBanner
						fetchErrorCode={fetchErrorCode}
					/>

					{failedImporting && <CSVExportSuggestion />}

					<CSVDownloadButton
						csvDownloadUrl={csvDownloadUrl}
						csvFilename={csvFilename}
					/>

					{readyToShowAdBanner &&
						!state.hasValidKopyPlusSubscription && (
							<div style={{ marginTop: "1rem" }}>
								<KopyPlusCTA
									overrideWidth="fit-content"
									context="single"
								/>
							</div>
						)}
				</div>

				<UploadMultipleProducts
					productsUrl={productsUrl}
					productsToUpload={productsToUpload}
					readyToUpload={readyToUpload}
					onFinishedUploading={onFinishedUploading}
					onFailedUploading={() => setFailedImporting(true)}
					onStartedRetry={() => setLoading(true)}
					hasImportedAProduct={state.hasImportedAProduct}
					store={state.shop}
					bulkAIOrders={bulkAIOrders}
				/>
			</div>

			<MultipleProductsImportModal
				modalIsOpen={modalIsOpen}
				productsUrl={productsUrl}
				products={cleanedProducts}
				submit={(products, bulkAIOrders) =>
					submitProductsToUpload(products, bulkAIOrders)
				}
				onModalClose={() => setModalIsOpen(false)}
				onCsvExport={onCsvExport}
			/>

			{productUrls?.length &&
			fetchedProducts.length &&
			productUrls.length !== fetchedProducts.length &&
			showMultilinkToast ? (
				<Toast
					content={`${t("multiple.not_all_products_obtained")} ${fetchedProducts.length}/${productUrls.length}`}
					onDismiss={() => setShowMultilinkToast(false)}
					duration={5000}
				/>
			) : (
				""
			)}
		</div>
	)
}

function cleanupHTML(html) {
	if (!html) return null

	const parser = new DOMParser()
	const doc = parser.parseFromString(html, "text/html")

	doc.querySelectorAll("*").forEach((el) => {
		;[...el.attributes].forEach((attr) => {
			if (attr.name.startsWith("data-canva-")) {
				el.removeAttribute(attr.name)
			}
		})
	})

	return doc.body.innerHTML.trim()
}

export default Multiple
